import React, { useEffect, useMemo, useState } from 'react';
import './FinancialDataDictionary.css';

const DATA_URL = `${process.env.PUBLIC_URL || ''}/reference-data/stockvisualise-financial-data/v1/data-dictionary.json`;
const EMPTY_FILTERS = {
  category: '',
  namespace: '',
  periodType: '',
  unit: '',
};
const EMPTY_ENTRIES = [];
const EMPTY_CATEGORY_ORDER = [];

const canonicalConcept = entry => `${entry.namespace}:${entry.concept}`;

const formatOption = value => value.replace(/(^|[-_])\w/g, match => match.toUpperCase());

const formatExampleValue = value => new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 10,
}).format(value);

const getOrderedCategories = (entries, categoryOrder) => {
  const available = new Set(entries.map(entry => entry.category));
  const ordered = categoryOrder.filter(category => available.has(category));
  const extras = [...available].filter(category => !categoryOrder.includes(category)).sort();
  return [...ordered, ...extras];
};

function DictionarySection({ id, title, description, count, noun, searchKey, children }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (searchKey) setOpen(true);
  }, [searchKey]);

  return (
    <section className="financial-data-dictionary-results" aria-labelledby={`${id}-title`}>
      <h2 id={`${id}-title`} className="financial-data-dictionary-section-title">
        <button type="button" aria-expanded={open} aria-controls={`${id}-content`} onClick={() => setOpen(value => !value)}>
          <span className="financial-data-dictionary-chevron" aria-hidden="true">›</span>
          <span>{title}</span>
        </button>
      </h2>
      <p className="financial-data-dictionary-section-description">{description}</p>
      <p className="financial-data-dictionary-count" aria-live="polite">{count} {noun}{count === 1 ? '' : 's'} found</p>
      <div id={`${id}-content`} hidden={!open}>{children}</div>
    </section>
  );
}

export default function FinancialDataDictionary() {
  const [dictionary, setDictionary] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  useEffect(() => {
    const controller = new AbortController();

    const loadDictionary = async () => {
      setDictionary(null);
      setLoadError('');

      try {
        const response = await fetch(DATA_URL, { signal: controller.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        if (!data.metadata || !Array.isArray(data.entries) || !Array.isArray(data.category_order)
          || !Array.isArray(data.calculated_metrics)
          || data.calculated_metrics.some(metric => !metric || !metric.concept || !metric.label
            || metric.namespace !== 'stockvisualise' || !metric.formula || !metric.period_type
            || !Array.isArray(metric.inputs) || !Array.isArray(metric.used_in))) {
          throw new Error('Invalid financial data dictionary format');
        }
        setDictionary(data);
      } catch (error) {
        if (error.name !== 'AbortError') {
          setLoadError('Financial data could not be loaded.');
        }
      }
    };

    loadDictionary();
    return () => controller.abort();
  }, [loadAttempt]);

  const entries = dictionary?.entries || EMPTY_ENTRIES;
  const metrics = dictionary?.calculated_metrics || EMPTY_ENTRIES;
  const allEntries = useMemo(() => [...entries, ...metrics], [entries, metrics]);
  const categoryOrder = dictionary?.category_order || EMPTY_CATEGORY_ORDER;

  const filterOptions = useMemo(() => ({
    categories: getOrderedCategories(allEntries, categoryOrder),
    namespaces: [...new Set(allEntries.map(entry => entry.namespace))].sort(),
    periodTypes: [...new Set(allEntries.map(entry => entry.period_type))].sort(),
    units: [...new Set(allEntries.map(entry => entry.unit))].sort(),
  }), [allEntries, categoryOrder]);

  const filteredEntries = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase();

    return allEntries.filter(entry => {
      const searchableText = [
        entry.label,
        canonicalConcept(entry),
        entry.description,
        entry.category,
        entry.formula,
        entry.basis,
        ...(entry.inputs || []).map(input => `${input.label} ${input.field}`),
      ].filter(Boolean).join(' ').toLocaleLowerCase();

      return (!normalizedQuery || searchableText.includes(normalizedQuery))
        && (!filters.category || entry.category === filters.category)
        && (!filters.namespace || entry.namespace === filters.namespace)
        && (!filters.periodType || entry.period_type === filters.periodType)
        && (!filters.unit || entry.unit === filters.unit);
    });
  }, [allEntries, filters, query]);

  const filteredConcepts = filteredEntries.filter(entry => entry.namespace !== 'stockvisualise');
  const filteredMetrics = filteredEntries.filter(entry => entry.namespace === 'stockvisualise')
    .sort((left, right) => left.label.localeCompare(right.label));

  const groupedEntries = getOrderedCategories(filteredConcepts, categoryOrder)
    .map(category => ({
      category,
      entries: filteredConcepts.filter(entry => entry.category === category),
    }));

  const namespaceCounts = useMemo(() => entries.reduce((counts, entry) => ({
    ...counts,
    [entry.namespace]: (counts[entry.namespace] || 0) + 1,
  }), {}), [entries]);

  const updateFilter = event => {
    const { name, value } = event.target;
    setFilters(current => ({ ...current, [name]: value }));
  };

  const clearFilters = () => {
    setQuery('');
    setFilters(EMPTY_FILTERS);
  };

  if (loadError) {
    return (
      <section className="financial-data-dictionary-page financial-data-dictionary-state" aria-labelledby="financial-data-dictionary-title">
        <h1 id="financial-data-dictionary-title">StockVisualise Financial Data</h1>
        <p role="alert">{loadError}</p>
        <button type="button" onClick={() => setLoadAttempt(attempt => attempt + 1)}>
          Try again
        </button>
      </section>
    );
  }

  if (!dictionary) {
    return (
      <section className="financial-data-dictionary-page financial-data-dictionary-state" aria-labelledby="financial-data-dictionary-title">
        <h1 id="financial-data-dictionary-title">StockVisualise Financial Data</h1>
        <p role="status">Loading financial concepts…</p>
      </section>
    );
  }

  const { metadata } = dictionary;
  const hasActiveFilters = query || Object.values(filters).some(Boolean);
  const searchKey = hasActiveFilters ? JSON.stringify([query, filters]) : '';

  return (
    <div className="financial-data-dictionary-page">
      <header className="financial-data-dictionary-hero">
        <p className="financial-data-dictionary-eyebrow">Data and API Documentation</p>
        <h1>StockVisualise Financial Data</h1>
        <p className="financial-data-dictionary-intro">
          Understand what companies report and how StockVisualise calculates financial metrics.
          Search both collections, or open a section to explore.
        </p>
      </header>

      <section className="financial-data-dictionary-controls" aria-labelledby="financial-data-dictionary-filter-title">
        <div className="financial-data-dictionary-controls-heading">
          <div>
            <h2 id="financial-data-dictionary-filter-title">Find a concept or metric</h2>
            <p>Search names, definitions, formulas, or input fields.</p>
          </div>
          <button type="button" onClick={clearFilters} disabled={!hasActiveFilters}>
            Clear filters
          </button>
        </div>

        <div className="financial-data-dictionary-filter-grid">
          <label className="financial-data-dictionary-search-field">
            <span>Search concepts and metrics</span>
            <input
              type="search"
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder="e.g. P/E, revenue growth, or Assets"
            />
          </label>

          <details className="financial-data-dictionary-more-filters">
            <summary>More filters</summary>
            <p>Filters apply to both collections. Choose a namespace to view one collection only.</p>
            <div className="financial-data-dictionary-filter-options">
          <label>
            <span>Category</span>
            <select name="category" value={filters.category} onChange={updateFilter}>
              <option value="">All categories</option>
              {filterOptions.categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </label>

          <label>
            <span>Namespace</span>
            <select name="namespace" value={filters.namespace} onChange={updateFilter}>
              <option value="">All namespaces</option>
              {filterOptions.namespaces.map(namespace => (
                <option key={namespace} value={namespace}>{namespace}</option>
              ))}
            </select>
          </label>

          <label>
            <span>Period type</span>
            <select name="periodType" value={filters.periodType} onChange={updateFilter}>
              <option value="">All period types</option>
              {filterOptions.periodTypes.map(periodType => (
                <option key={periodType} value={periodType}>{formatOption(periodType)}</option>
              ))}
            </select>
          </label>

          <label>
            <span>Unit</span>
            <select name="unit" value={filters.unit} onChange={updateFilter}>
              <option value="">All units</option>
              {filterOptions.units.map(unit => <option key={unit} value={unit}>{unit}</option>)}
            </select>
          </label>
            </div>
          </details>
        </div>
      </section>

      <DictionarySection id="reported-concepts" title="Company-reported financial concepts"
        description="Fields reported in company filings, grouped by financial topic."
        count={filteredConcepts.length} noun="concept" searchKey={searchKey}>
        <p className="financial-data-dictionary-section-description">
          Examples from {metadata.baseline_company}'s FY{metadata.baseline_fiscal_year} filing.
          This baseline is not a complete taxonomy; available concepts vary by company.
        </p>
        <dl className="financial-data-dictionary-source" aria-label="Source summary">
          <div><dt>Filing</dt><dd>{metadata.source_form}, {metadata.source_filed}</dd></div>
          <div><dt>Baseline</dt><dd>{metadata.baseline_company}</dd></div>
          <div><dt>Namespaces</dt><dd>{Object.entries(namespaceCounts).map(([name, count]) => `${name} ${count}`).join(' · ')}</dd></div>
        </dl>

        {filteredConcepts.length === 0 ? (
          <div className="financial-data-dictionary-empty">
            <h3>No concepts found</h3>
            <p>Try a broader search or clear filters.</p>
          </div>
        ) : groupedEntries.map(group => (
          <details
            className="financial-data-dictionary-group"
            key={group.category}
            open={hasActiveFilters || undefined}
          >
            <summary className="financial-data-dictionary-group-heading">
              <span className="financial-data-dictionary-group-title" role="heading" aria-level="3">
                {group.category}
              </span>
              <span className="financial-data-dictionary-group-count">{group.entries.length}</span>
            </summary>

            <div className="financial-data-dictionary-concepts">
              {group.entries.map(entry => {
                const conceptKey = canonicalConcept(entry);
                const period = entry.start ? `${entry.start} to ${entry.end}` : entry.end;

                return (
                  <details className="financial-data-dictionary-concept" key={conceptKey}>
                    <summary>
                      <span className="financial-data-dictionary-concept-heading">
                        <span className={`financial-data-dictionary-label${entry.label ? '' : ' is-missing'}`}>
                          {entry.label || 'Label not supplied'}
                        </span>
                        <code>{conceptKey}</code>
                      </span>
                      <span className="financial-data-dictionary-chips" aria-hidden="true">
                        <span>{formatOption(entry.period_type)}</span>
                        <span>{entry.unit}</span>
                      </span>
                    </summary>

                    <div className="financial-data-dictionary-details">
                      <div className="financial-data-dictionary-definition">
                        <h4>Definition</h4>
                        <p>{entry.description || 'Definition not supplied.'}</p>
                      </div>

                      <dl>
                        <div><dt>Category</dt><dd>{entry.category}</dd></div>
                        <div><dt>Namespace</dt><dd>{entry.namespace}</dd></div>
                        <div><dt>Unit</dt><dd>{entry.unit}</dd></div>
                        <div><dt>Period type</dt><dd>{formatOption(entry.period_type)}</dd></div>
                        <div><dt>Amazon FY{entry.fy} example</dt><dd>{formatExampleValue(entry.value)}</dd></div>
                        <div><dt>Example period</dt><dd>{period}</dd></div>
                        <div><dt>Filing</dt><dd>{entry.form}, filed {entry.filed}</dd></div>
                        <div><dt>Accession</dt><dd><code>{entry.accession}</code></dd></div>
                        {entry.frame && <div><dt>Frame</dt><dd><code>{entry.frame}</code></dd></div>}
                        <div className="financial-data-dictionary-selection-basis">
                          <dt>Selection basis</dt><dd>{entry.selection_basis}</dd>
                        </div>
                      </dl>
                    </div>
                  </details>
                );
              })}
            </div>
          </details>
        ))}
      </DictionarySection>

      <DictionarySection id="calculated-metrics" title="Financial metrics and calculations"
        description="Ratios, valuations, and growth measures calculated by StockVisualise."
        count={filteredMetrics.length} noun="metric" searchKey={searchKey}>
        <p className="financial-data-dictionary-section-description">
          Expand a metric for its formula, inputs, period, and limitations. Annual valuation ratios use
          fiscal-year-end prices; they are not live or trailing-twelve-month valuations.
        </p>
        {filteredMetrics.length === 0 ? (
          <div className="financial-data-dictionary-empty">
            <h3>No metrics found</h3>
            <p>Try a broader search or clear filters.</p>
          </div>
        ) : (
          <div className="financial-data-dictionary-concepts">
            {filteredMetrics.map(metric => (
              <details className="financial-data-dictionary-concept" key={canonicalConcept(metric)}>
                <summary>
                  <span className="financial-data-dictionary-concept-heading">
                    <span className="financial-data-dictionary-label">{metric.label}</span>
                    <span className="financial-data-dictionary-metric-description">{metric.description}</span>
                  </span>
                  <span className="financial-data-dictionary-chips" aria-hidden="true"><span>{metric.category}</span></span>
                </summary>
                <div className="financial-data-dictionary-details">
                  <div className="financial-data-dictionary-definition">
                    <h3>Formula</h3>
                    <p className="financial-data-dictionary-formula">{metric.formula}</p>
                  </div>
                  <dl>
                    <div><dt>Metric key</dt><dd><code>{canonicalConcept(metric)}</code></dd></div>
                    <div><dt>Unit</dt><dd>{metric.unit}</dd></div>
                    <div className="financial-data-dictionary-selection-basis"><dt>Inputs</dt><dd>
                      <ul>{metric.inputs.map(input => <li key={input.field}>{input.label}<br /><code>{input.field}</code></li>)}</ul>
                    </dd></div>
                    <div><dt>Period and basis</dt><dd>{metric.basis}</dd></div>
                    <div><dt>Missing or negative values</dt><dd>{metric.handling}</dd></div>
                    <div className="financial-data-dictionary-selection-basis"><dt>Where to find it</dt><dd>
                      <ul>{metric.used_in.map(usage => <li key={usage.path}><a href={usage.path}>{usage.label}</a></li>)}</ul>
                    </dd></div>
                  </dl>
                </div>
              </details>
            ))}
          </div>
        )}
      </DictionarySection>
    </div>
  );
}
